/**
 * Shared API client for web and mobile.
 *
 * Two surfaces over one config:
 *  - `client` — a typed openapi-fetch client generated from `openapi.yaml`
 *    (`./schema`). Prefer this for new code: paths, params, and payloads are
 *    checked against the spec. Pair it with `unwrap()` for throw-on-error
 *    ergonomics that React Query expects.
 *  - `api` — the legacy string-path wrapper (`api.get/post/put/del`). Kept as a
 *    back-compat shim while hooks migrate to `client` (Monday #11961460766
 *    follow-up). Both share auth, base URL, the request timeout, and ApiError.
 *
 * Web: same-origin fetch, Clerk __session cookie rides via credentials: 'include'.
 * Mobile: configureApi({ baseUrl, getToken }) at boot — adds Authorization: Bearer.
 */

import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'

export type { paths, components } from './schema'

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(status: number, message: string, data: unknown = null) {
    super(message)
    this.status = status
    this.data = data
  }
}

type JsonBody = Record<string, unknown> | unknown[] | null | object

type ApiConfig = {
  baseUrl?: string
  getToken?: () => Promise<string | null> | string | null
  // Called once after a 401 to force-mint a fresh token (e.g. Clerk
  // getToken({ skipCache: true })). The request is retried a single time with
  // the result. Distinct from getToken, which may return a still-cached token
  // that the server just rejected.
  refreshToken?: () => Promise<string | null> | string | null
}

let config: ApiConfig = {}

export function configureApi(c: ApiConfig): void {
  config = { ...config, ...c }
}

export function getApiConfig(): Readonly<ApiConfig> {
  return config
}

// Hard cap on any request. Without this, fetch can stall indefinitely on a
// dropped TCP connection or a server that accepted the request but never
// flushed a response — which silently softlocks the UI behind React Query's
// `isPending` flag (no retry, no error, no resolution).
const REQUEST_TIMEOUT_MS = 20_000

async function request<T>(
  method: string,
  path: string,
  body?: JsonBody,
  init?: RequestInit,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  }

  const token = config.getToken ? await config.getToken() : null
  if (token) headers.Authorization = `Bearer ${token}`

  let payload: BodyInit | undefined
  if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  // Strip any trailing slash from baseUrl so `https://pelvia.me/` + `/api/x`
  // doesn't produce `https://pelvia.me//api/x`. Some servers normalize that
  // away, some don't (and it's noisy in logs either way).
  const trimmedBase = config.baseUrl ? config.baseUrl.replace(/\/+$/, '') : ''
  const url = trimmedBase ? `${trimmedBase}${path}` : path

  const callerSignal = init?.signal as AbortSignal | undefined

  // One attempt = one fetch with its own timeout. We make a second attempt only
  // when the first returns 401 and a refreshToken is configured, so a token
  // that expired in-flight gets transparently retried instead of surfacing as
  // a spurious auth failure to the UI.
  const attempt = async (): Promise<Response> => {
    // Wire the timeout to the caller-provided signal if they passed one — for
    // SSE / coach streaming we still want the caller's abort to win.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort()
      else callerSignal.addEventListener('abort', () => controller.abort(), { once: true })
    }
    try {
      return await fetch(url, {
        method,
        credentials: 'include',
        headers,
        body: payload,
        ...init,
        signal: controller.signal,
      })
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        throw new ApiError(
          0,
          callerSignal?.aborted ? 'Request cancelled' : 'Request timed out',
          null,
        )
      }
      throw new ApiError(0, (err as Error)?.message ?? 'Network error', null)
    } finally {
      clearTimeout(timer)
    }
  }

  let res = await attempt()
  if (res.status === 401 && config.refreshToken) {
    const fresh = await config.refreshToken()
    if (fresh) {
      headers.Authorization = `Bearer ${fresh}`
      res = await attempt()
    }
  }

  const isJson = res.headers.get('content-type')?.includes('application/json')
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null)

  if (!res.ok) {
    const msg =
      (isJson && data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)
        ? String((data as Record<string, unknown>).error)
        : null) ?? `Request failed (${res.status})`
    throw new ApiError(res.status, msg, data)
  }

  return data as T
}

export const api = {
  get: <T = unknown>(path: string, init?: RequestInit) => request<T>('GET', path, null, init),
  post: <T = unknown>(path: string, body?: JsonBody, init?: RequestInit) =>
    request<T>('POST', path, body ?? {}, init),
  put: <T = unknown>(path: string, body?: JsonBody, init?: RequestInit) =>
    request<T>('PUT', path, body ?? {}, init),
  del: <T = unknown>(path: string, body?: JsonBody, init?: RequestInit) =>
    request<T>('DELETE', path, body ?? null, init),
}

/**
 * GET a binary file (e.g. the consult-note PDF) with the same auth as `api`.
 *
 * `request()` reads the body as JSON/text, which corrupts binary, so file
 * downloads come through here instead. Platform-agnostic — returns the Blob and
 * the server-suggested filename; the caller decides how to save it (web: object
 * URL + anchor; mobile: share sheet / file system).
 */
export async function downloadBlob(path: string): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = { Accept: '*/*' }
  const token = config.getToken ? await config.getToken() : null
  if (token) headers.Authorization = `Bearer ${token}`

  const trimmedBase = config.baseUrl ? config.baseUrl.replace(/\/+$/, '') : ''
  const url = trimmedBase ? `${trimmedBase}${path}` : path

  const res = await fetch(url, { method: 'GET', headers, credentials: 'include' })
  if (!res.ok) {
    const isJson = res.headers.get('content-type')?.includes('application/json')
    const data = isJson ? await res.json().catch(() => null) : null
    const msg =
      (data && typeof data === 'object' && 'error' in data ? String(data.error) : null) ??
      `Download failed (${res.status})`
    throw new ApiError(res.status, msg, data)
  }

  const disposition = res.headers.get('content-disposition') || ''
  const match = /filename="?([^"]+)"?/.exec(disposition)
  return { blob: await res.blob(), filename: match?.[1] || 'download' }
}

// ---------------------------------------------------------------------------
// Typed client (openapi-fetch)
// ---------------------------------------------------------------------------

/**
 * Custom fetch for the typed client. Adds the same 20s hard timeout as the
 * legacy `request()` path, and rebases relative URLs onto `config.baseUrl`.
 *
 * The rebase matters on mobile: openapi-fetch builds the request with an empty
 * base, so on React Native the URL stays relative (`/api/...`) and would have
 * no host. On web the platform already resolves it to an absolute same-origin
 * URL, so the rebase is a no-op there.
 */
async function timeoutFetch(input: Request): Promise<Response> {
  const base = config.baseUrl ? config.baseUrl.replace(/\/+$/, '') : ''
  const request =
    base && input.url.startsWith('/') ? new Request(`${base}${input.url}`, input) : input

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  const callerSignal = request.signal
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort()
    else callerSignal.addEventListener('abort', () => controller.abort(), { once: true })
  }

  try {
    return await fetch(request, { signal: controller.signal })
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new ApiError(
        0,
        callerSignal?.aborted ? 'Request cancelled' : 'Request timed out',
        null,
      )
    }
    throw new ApiError(0, (err as Error)?.message ?? 'Network error', null)
  } finally {
    clearTimeout(timer)
  }
}

// Inject the Clerk bearer token on every request. Web also rides the __session
// cookie via `credentials: 'include'` (set as a client default below).
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = config.getToken ? await config.getToken() : null
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
  async onResponse({ request, response }) {
    // Mirror the legacy request() retry: a 401 with a configured refreshToken
    // means the token likely expired in-flight. Re-mint and retry once. Only
    // replay safe (bodyless) methods so we never double-submit a mutation — the
    // observed symptom was GET dashboard/profile 401s anyway.
    if (response.status !== 401 || !config.refreshToken) return response
    if (request.method !== 'GET' && request.method !== 'HEAD') return response

    const fresh = await config.refreshToken()
    if (!fresh) return response

    const headers = new Headers(request.headers)
    headers.set('Authorization', `Bearer ${fresh}`)
    return timeoutFetch(
      new Request(request.url, {
        method: request.method,
        headers,
        credentials: 'include',
        signal: request.signal,
      }),
    )
  },
}

/**
 * Typed API client. Generated from `openapi.yaml` — regenerate after a spec
 * change with `pnpm --filter @pelvia/shared codegen`.
 */
export const client = createClient<paths>({
  credentials: 'include',
  fetch: timeoutFetch,
})
client.use(authMiddleware)

type FetchResult<T> = { data?: T; error?: unknown; response: Response }

/**
 * Unwrap an openapi-fetch result: returns `data` on success, throws `ApiError`
 * on failure. Lets typed-client calls drop straight into a React Query
 * `queryFn` / `mutationFn`, which expect a thrown error rather than `{ error }`.
 */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await call
  if (error !== undefined || !response.ok) {
    const message =
      error && typeof error === 'object' && 'error' in error
        ? String((error as { error: unknown }).error)
        : `Request failed (${response.status})`
    throw new ApiError(response.status, message, error ?? null)
  }
  return data as T
}
